import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized } from 'vue-router'
import useUserStore from '@/stores/user';
import { topNavItems, bottomNavItems, isVisible } from '@/components/bui/organisms/Nav/navItems.config';

const navItems = [...topNavItems, ...bottomNavItems];

/**
 * 'home' is the fallback out of not-found, so gating it would loop.
 * 'user-editor' is "My Account" — under /users by URL only, not by permission.
 */
const UNGATED_ROUTES = new Set(['home', 'user-editor']);

/** Never captured into ?redirect=: '/login?redirect=/not-found' is a dead end,
 *  and it has already overwritten the page the user actually wanted. */
const NON_RETURNABLE_ROUTES = new Set(['login', 'not-found', 'catch-all']);

/** Whether this route is worth returning to after signing in. */
export function isReturnableRoute(name: unknown) {
  return !NON_RETURNABLE_ROUTES.has(String(name));
}

/**
 * A page is reachable by URL only when it is reachable from the nav.
 * 
 * Matched on the first path segment: '/players/edit/12' answers to "Players".
 * A path no menu item claims is open.
 */
function canAccessPath(path: string, user: any) {
  const segment = '/' + (path.split('/')[1] ?? '');

  for (const item of navItems) {
    const subItem = item.subItems?.find((sub) => sub.path === segment);
    const isClaimedByThisItem = item.path === segment || subItem;
    if (!isClaimedByThisItem) continue;

    const itemAndItsParent = subItem ? [item, subItem] : [item];
    // Same check the nav uses to show the item.
    return itemAndItsParent.every((entry) => isVisible(entry, user));
  }
  return true;   // unclaimed by the menu
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('../views/DashboardView/DashboardView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
      meta: { requiresAuth: false, transition: 'slide-right' }
    },
    // {
    //   path: '/users/edit/:id',
    //   name: 'edit-user',
    //   component: () => import('../views/MediaEditorView/MediaEditorView.vue'),
    //   meta: { requiresAuth: true }
    // },
    
    // Lists pages
    {
      path: '/media',
      name: 'media',
      component: () => import('../views/MediaListView/MediaListView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/playlists',
      name: 'playlists',
      component: () => import('../views/PlaylistsListView/PlaylistsListView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/campaigns',
      name: 'campaigns',
      component: () => import('../views/CampaignsListView/CampaignsListView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/players',
      name: 'players',
      component: () => import('../views/PlayersListView/PlayersListView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/player-subscriptions',
      name: 'player-subscriptions',
      component: () => import('../views/PlayerSubscriptionsListView/PlayerSubscriptionsListView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/live-streams',
      name: 'live-streams',
      component: () => import('../views/LiveStreamsListView/LiveStreamsListView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/analytics',
      name: 'analytics',
      component: () => import('../views/AnalyticsView/AnalyticsView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/reports',
      name: 'reports',
      component: () => import('../views/ReportsView/ReportsView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: () => import('../views/NotificationsView/NotificationsView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/notification-settings',
      name: 'notification-settings',
      component: () => import('../views/SettingsViews/NotificationSettingsView/NotificationSettingsView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/pos-sources',
      name: 'pos-sources',
      component: () => import('../views/POSSourcesListView/POSSourcesListView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/organizations',
      name: 'organizations',
      component: () => import('../views/SettingsViews/OrganizationsListView/OrganizationsListView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/locations',
      name: 'locations',
      component: () => import('../views/SettingsViews/LocationsListView/LocationsListView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('../views/SettingsViews/UsersListView/UsersListView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/users/create',
      name: 'users-create',
      component: () => import('../views/SettingsViews/UsersListView/CreateUserForm/CreateUserForm.vue'),
      meta: { requiresAuth: true, createAccessArea: 'Administration', transition: 'slide-right' }
    },
    
    {
      path: '/roles',
      name: 'roles',
      component: () => import('../views/SettingsViews/RolesListView/RolesListView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/roles/create',
      name: 'roles-create',
      component: () => import('../views/SettingsViews/RolesListView/CreateRoleForm/CreateRoleForm.vue'),
      meta: { requiresAuth: true, createAccessArea: 'AccountSettings', transition: 'slide-right' }
    },
    {
      path: '/auditing',
      name: 'auditing',
      component: () => import('../views/SettingsViews/AuditingListView/AuditingListView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },

    // Editors pages
    {
      path: '/media/edit/:id',
      name: 'media-editor',
      component: () => import('../views/MediaEditorView/MediaEditorView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/playlists/edit/:id',
      name: 'playlist-editor',
      component: () => import('../views/PlaylistEditorView/PlaylistEditorView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/campaigns/edit/:id',
      name: 'campaign-editor',
      component: () => import('../views/CampaignEditorView/CampaignEditorView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/players/edit/:id',
      name: 'player-editor',
      component: () => import('../views/PlayerEditorView/PlayerEditorView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/pos-sources/edit/:id',
      name: 'pos-source-editor',
      component: () => import('../views/POSSourceEditorView/POSSourceEditorView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/player-subscriptions/edit/:id',
      name: 'player-subscription-editor',
      component: () => import('../views/PlayerSubscriptionEditorView/PlayerSubscriptionEditorView.vue'),
      meta: { requiresAuth: true , resource: 'playersubscriptions' }
    },
    {
      path: '/live-streams/edit/:id',
      name: 'live-stream-editor',
      component: () => import('../views/LiveStreamEditorView/LiveStreamEditorView.vue'),
      meta: { requiresAuth: true , resource: 'playersubscriptions' }
    },
    {
      path: '/organizations/edit/:id',
      name: 'organization-editor',
      component: () => import('../views/SettingsViews/OrganizationEditorView/OrganizationEditorView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/locations/edit/:id',
      name: 'location-editor',
      component: () => import('../views/SettingsViews/LocationEditorView/LocationEditorView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/users/edit/:id',
      name: 'user-editor',
      component: () => import('../views/SettingsViews/UsersEditorView/UserEditorForm.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/roles/edit/:id',
      name: 'role-editor',
      component: () => import('../views/SettingsViews/RoleEditorView/RoleEditorView.vue'),
      // TODO: think about making a generic component "SimpleEntityEditor" that will be used for all simple entities — N2MErZC3
      meta: { requiresAuth: true }
    },
    {
      // Client-level settings (formerly "Account"; BSPORTAL-3057)
      path: '/client',
      name: 'client',
      component: () => import('../views/AccountEditorView/AccountEditorView.vue'),
      meta: { requiresAuth: true }
    },
    {
      // Old URL, kept for bookmarks and the QA suite
      path: '/account',
      redirect: '/client',
    },
    {
      path: "/Account/ChangePassword",
      name: "reset-password",
      component: () => import('../views/ResetPasswordView/ResetPasswordView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/knowledge-base',
      name: 'knowledge-base',
      component: () => import('../views/KnowledgeBaseView/KnowledgeBaseView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    {
      path: '/knowledge-base/article/:id',
      name: 'knowledge-base-article',
      component: () => import('../views/KnowledgeBaseView/KnowledgeBaseArticleView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/not-found',
      name: 'not-found',
      component: () => import('../views/NotFoundView/NotFoundView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/bulkregistration/:deviceUuid',
      name: 'bulk-registration',
      component: () => import('../views/BulkRegistrationView/BulkRegistrationView.vue'),
      meta: {
        requiresAuth: false,
        standalone: true
      }
    },
    {
      path: '/settings',
      name: 'settings',
      redirect: '/organizations',
      meta: { requiresAuth: true }
    },
    {
      path: '/uptime',
      name: 'uptime',
      component: () => import('../views/UptimeView/UptimeView.vue'),
      meta: { requiresAuth: true, transition: 'slide-right' }
    },
    // Keep last: anything unmatched is a not-found, same as a record we can't read.
    {
      path: '/:pathMatch(.*)*',
      name: 'catch-all',
      component: () => import('../views/NotFoundView/NotFoundView.vue'),
      meta: { requiresAuth: true }
    },
  ]
})

// currentRoute only changes once every guard has resolved, and some editors load the
// new record inside an awaited onBeforeRouteUpdate. Requests made meanwhile belong to
// the route being navigated to.
let pendingRoute: RouteLocationNormalized | undefined;

/** The route being navigated to, if a navigation is in progress; otherwise the current one. */
export function routeInProgress() {
  return pendingRoute ?? router.currentRoute.value;
}

router.beforeEach((to) => {
  pendingRoute = to;
})
// Runs for completed, aborted and cancelled navigations alike.
router.afterEach(() => {
  pendingRoute = undefined;
})
router.onError(() => {
  pendingRoute = undefined;
})

router.beforeEach((to) => {
  const user = useUserStore()
  // Restore session from localStorage before auth decisions so deep links
  // and refreshes don't race ahead of onMounted-based checkLogin().
  user.checkLogin()

  if (!to.meta.requiresAuth) return

  if (!user.isLoggedIn) {
    return isReturnableRoute(to.name)
      ? { name: 'login', query: { redirect: to.fullPath } }
      : { name: 'login' }
  }

  const isGated = !UNGATED_ROUTES.has(String(to.name));
  if (isGated && !canAccessPath(to.path, user)) {
    // No 'from': this account cannot reach the list either, so NotFoundView
    // falls back to home.
    return { name: 'not-found' }
  }

  // The area the backend checks when the form is saved. This account can still
  // reach the list, so not-found offers it as the way back.
  const createAccessArea = to.meta.createAccessArea as string | undefined;
  if (createAccessArea && !user.hasCreateAccessToArea(createAccessArea)) {
    return { name: 'not-found', query: { from: to.path.replace(/\/create\/?$/, '') } }
  }
})

export default router
